import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Campaign {
  id: string
  campaign_code: string
  name: string
  type: string
  status: string
  start_date: string
  end_date: string
  budget: number
  spent: number
  leads_generated: number
  conversions: number
  channel: string
}

interface Lead {
  id: string
  lead_id: string
  company: string
  contact_name: string
  source: string
  status: string
  score: number
  estimated_value: number
  assigned_to: string
}

export default function MarketingPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [campRes, leadRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/marketing/campaigns'),
      fetch(API_BASE + '/marketing/leads'),
      fetch(API_BASE + '/marketing/statistics')
    ])
    if (campRes.ok) setCampaigns((await campRes.json()).campaigns || [])
    if (leadRes.ok) setLeads((await leadRes.json()).leads || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'won': case 'qualified': return 'bg-green-100 text-green-800'
      case 'scheduled': case 'contacted': case 'proposal': return 'bg-blue-100 text-blue-800'
      case 'draft': case 'new': case 'negotiation': return 'bg-yellow-100 text-yellow-800'
      case 'paused': case 'lost': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Campaign</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Campaigns</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_campaigns}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Leads</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_leads}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Conversion Rate</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.conversion_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Cost per Lead</div>
              <div className="text-2xl font-bold">${statistics.cost_per_lead}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">ROI</div>
              <div className="text-2xl font-bold text-green-600">{statistics.roi}x</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Campaigns</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {campaigns.slice(0, 12).map(campaign => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{campaign.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{campaign.type} - {campaign.channel}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">${campaign.spent.toLocaleString()} / ${campaign.budget.toLocaleString()}</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-blue-600 h-1.5 rounded-full" style={{width: Math.min((campaign.spent / campaign.budget) * 100, 100) + '%'}}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{campaign.leads_generated} leads</div>
                      <div className="text-xs text-green-600">{campaign.conversions} conversions</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(campaign.status)}`}>{campaign.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Leads</h3>
            <div className="space-y-3">
              {leads.slice(0, 10).map(lead => (
                <div key={lead.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{lead.company}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(lead.status)}`}>{lead.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{lead.contact_name}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">Score: {lead.score}</span>
                    <span className="text-xs font-medium">${lead.estimated_value.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
